import React from "react";
import { useTranslation } from "react-i18next";
import TextDiv from "./TextDiv";
import SectionDivider from "./SectionDivider";
import CollapsibleCard from "../pages/attendance-system/CollapsibleCard";
import RenderMatchStatus from "./RenderMatchStatus";
import RenderMatchPercentage from "./RenderMatchPercentage";
import SectionDividerWithNoLine from "./SectionDividerWithNoLine";
import { formatString } from "../utils/formatString";
import { extractNumericValue } from "../utils/extractNumericValues";
import { hasCurrencySymbol } from "../utils/currencySymbolChecker";

const ComparisonField = ({
    title,
    value,
    compareValue,
    showMatchPercentage = true,
    textDivProps = {},
    matchPercentageProps = {},
}) => {
    return (
        <div className="o-column o-column--gap-size_300">
            <div className="o-column o-column--gap-size_300" style={{ flex: 1 }}>
                <TextDiv
                    title={title}
                    value={value}
                    divClasses="o-column-row-switcher"
                    pClasses="t-bold_semi-bold u-width--min-width_300"
                    {...textDivProps}
                />

                {showMatchPercentage && compareValue && (
                    <RenderMatchPercentage
                        value1={value}
                        value2={compareValue}
                        matchText="matchWithInvoice"
                        spanStyle="u-width--min-width_300"
                        {...matchPercentageProps}
                    />
                )}
            </div>
        </div>
    );
};

const ComparisonFieldWithStatus = ({
    title,
    value,
    compareValue,
    showMatchStatus = true,
    matchStatusProps = {},
}) => {
    return (
        <div className="o-column o-column--gap-size_300">
            <div className="o-column o-column--gap-size_300" style={{ flex: 1 }}>
                {showMatchStatus && (
                    <RenderMatchStatus
                        spanStyle="u-width--min-width_300"
                        {...matchStatusProps}
                    />
                )}
            </div>
        </div>
    );
};

const BugReportAnalysisViewer = ({ analyzedData }) => {
    const { t } = useTranslation("general");

    if (!analyzedData) return null;

    const supplierInvoiceDetails = analyzedData?.invoice?.supplier || {};
    const supplierWebsiteDetails = analyzedData?.supplierWebsiteScraper || {};
    const items = analyzedData?.invoice?.items || [];
    const supplierBankDetails = analyzedData?.bankForm?.supplier || {};
    const supplierCertificateDetails = analyzedData?.supplierCertificate || {};
    const supplierArticleOfAssociation = analyzedData?.articlesOfAssociation || {};

    return (
        <div className="o-column o-column--gap-size_400">

            {/* Supplier Section */}
            <div>
                <SectionDivider title={"supplier"} />
                <CollapsibleCard
                    title={`${t("supplierDetails")}`}
                    collapsed={true}
                    body={
                        <>
                            {/* Supplier Name */}
                            <section>
                                <SectionDivider title={t("supplierName")} noMargin />
                                <div className="o-column o-column--gap-size_500 u-margin--top_400">
                                    <TextDiv
                                        title="supplierNameInInvoice"
                                        value={supplierInvoiceDetails.name}
                                        divClasses="o-column-row-switcher"
                                        pClasses="t-bold_semi-bold u-width--min-width_300"
                                    />
                                    <ComparisonField
                                        title="supplierNameInBankApplication"
                                        value={supplierBankDetails.name}
                                        compareValue={supplierInvoiceDetails.name}
                                    />
                                    <ComparisonField
                                        title="supplierNameInWebsite"
                                        value={supplierWebsiteDetails.companyName}
                                        compareValue={supplierInvoiceDetails.name}
                                    />
                                    <ComparisonField
                                        title="supplierNameInCertificate"
                                        value={supplierCertificateDetails?.companyName}
                                        compareValue={supplierInvoiceDetails.name}
                                    />
                                </div>
                            </section>

                            {/* Address */}
                            <section className="u-margin--top_500">
                                <SectionDivider title={t("address")} />
                                <div className="o-column o-column--gap-size_500">
                                    <TextDiv
                                        title="addressInInvoice"
                                        value={supplierInvoiceDetails.address}
                                        divClasses="o-column-row-switcher"
                                        pClasses="t-bold_semi-bold u-width--min-width_300"
                                        spanClasses="dir-ltr t-text-align_center"
                                    />
                                    <ComparisonField
                                        title="addressInBankApplication"
                                        value={supplierBankDetails.address}
                                        compareValue={supplierInvoiceDetails.address}
                                        textDivProps={{ spanClasses: "dir-ltr t-text-align_center" }}
                                    />
                                    <ComparisonField
                                        title="addressInWebsite"
                                        value={supplierWebsiteDetails.companyAddress}
                                        compareValue={supplierInvoiceDetails.address}
                                        textDivProps={{ spanClasses: "dir-ltr t-text-align_center" }}
                                    />
                                </div>
                            </section>

                            {/* Phone */}
                            <section className="u-margin--top_500">
                                <SectionDivider title={t("phoneNumber")} />
                                <div className="o-column o-column--gap-size_500">
                                    <TextDiv
                                        title="phoneNumberInInvoice"
                                        value={supplierInvoiceDetails.phoneNumber}
                                        divClasses="o-column-row-switcher"
                                        pClasses="t-bold_semi-bold u-width--min-width_300"
                                        spanClasses="dir-ltr"
                                    />
                                    <ComparisonField
                                        title="phoneNumberInBankApplication"
                                        value={supplierBankDetails.phoneNumber}
                                        compareValue={supplierInvoiceDetails.phoneNumber}
                                        textDivProps={{ spanClasses: "dir-ltr" }}
                                        matchPercentageProps={{ spanClasses: "dir-ltr" }}
                                    />
                                    <ComparisonField
                                        title="phoneNumberInWebsite"
                                        value={supplierWebsiteDetails.phoneNumber}
                                        compareValue={supplierInvoiceDetails.phoneNumber}
                                        textDivProps={{ spanClasses: "dir-ltr" }}
                                    />
                                </div>
                            </section>
                        </>
                    }
                />
            </div>

            {/* Invoice Details */}
            <div>
                <SectionDivider title={"invoice"} />
                <CollapsibleCard
                    title={t("invoiceDetails")}
                    collapsed={true}
                    body={
                        <section>
                            <SectionDivider title={t("invoice")} noMargin />
                            <div className="o-column o-column--gap-size_400 u-margin--top_400">
                                <TextDiv
                                    title="invoiceNumber"
                                    value={analyzedData?.invoice?.invoiceNumber || ""}
                                    divClasses="o-column-row-switcher"
                                    pClasses="t-bold_semi-bold u-width--min-width_300"
                                />
                                <TextDiv
                                    title="invoiceDate"
                                    value={analyzedData?.invoice?.invoiceDate || ""}
                                    divClasses="o-column-row-switcher"
                                    pClasses="t-bold_semi-bold u-width--min-width_300"
                                />
                                <TextDiv
                                    title="totalAmountFromInvoice"
                                    value={
                                        hasCurrencySymbol(analyzedData?.invoice?.totalAmount)
                                            ? formatString(analyzedData?.invoice?.totalAmount)
                                            : `${formatString(analyzedData?.invoice?.totalAmount || "")} ${analyzedData?.invoice?.currency || ""}`
                                    }
                                    divClasses="o-column-row-switcher"
                                    pClasses="t-bold_semi-bold u-width--min-width_300"
                                />
                                <TextDiv
                                    title="calculatedTotalPrice"
                                    value={`${analyzedData?.invoice?.calculatedTotalPrice || ""} ${analyzedData?.invoice?.currency || ""}`}
                                    divClasses="o-column-row-switcher"
                                    pClasses="t-bold_semi-bold u-width--min-width_300"
                                />
                                <ComparisonFieldWithStatus
                                    title={"invoiceTotalAmountMatch"}
                                    matchStatusProps={{
                                        value1: extractNumericValue(analyzedData?.invoice?.totalAmount),
                                        value2: parseFloat(analyzedData?.invoice?.calculatedTotalPrice),
                                        matchText: "itemTotalAmountMatch",
                                    }}
                                />
                            </div>
                        </section>
                    }
                />
            </div>

            {/* Items Section */}
            <div>
                <SectionDivider title={"items"} />
                {items.map((item, index) => (
                    <div className="o-column o-column--gap-size_300 u-margin--top_600" key={index}>
                        <SectionDividerWithNoLine
                            title={`${t("item")} ${index + 1}`}
                            noMargin={true}
                            spanClasses="t-bold_extra-bold"
                        />
                        <TextDiv
                            title={"name/no"}
                            value={item["name/no"]}
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                            title="description"
                            value={item.description}
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                            title="quantity"
                            value={`${item.quantity} ${item.measuringUnit || ""}`}
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                            spanClasses="dir-ltr"
                        />
                        <TextDiv
                            title="unitPrice"
                            value={
                                hasCurrencySymbol(item?.unitPrice)
                                    ? formatString(item.unitPrice)
                                    : `${formatString(item.unitPrice)} ${analyzedData?.invoice?.currency}`
                            }
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <TextDiv
                            title="totalAmountFromInvoice"
                            value={
                                hasCurrencySymbol(item.totalPrice)
                                    ? formatString(item.totalPrice)
                                    : `${formatString(item.totalPrice)} ${analyzedData?.invoice?.currency}`
                            }
                            divClasses="o-column-row-switcher"
                            pClasses="t-bold_semi-bold u-width--min-width_300"
                        />
                        <ComparisonFieldWithStatus
                            title={`${"item"}${index + 1}${"TotalAmountMatch"}`}
                            matchStatusProps={{
                                value1: extractNumericValue(item.totalPrice),
                                value2: parseFloat(item.calculatedPrice),
                                matchText: "itemTotalAmountMatch",
                            }}
                        />
                    </div>
                ))}
            </div>

        </div>
    );
};

export default BugReportAnalysisViewer;
